import { Override_Options_Enum } from "gql/api";
import {
  Severity,
  valuesExist,
  addStackPrefixToMessage,
  type ValidationRuleGenerator
} from "../scenarioValidator";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import { MIN_CLEARANCE } from "../validatorConstants";

export const intakeChecksNames = [
  "intake_size_top_casing",
  "intake_size_bypass_tubing_top_casing",
  "intake_size_bottom_casing",
  "intake_size_bypass_tubing_bottom_casing",
  "intake_shaft_limit_hs",
  "intake_shaft_limit_std",
  "intake_use_natural_gas_separation"
] as const;

export const intakeChecks: ValidationRuleGenerator = ({
  calcs,
  parameters,
  wellProperties,
  equipment,
  stack
}) => {
  return [
    // Clearance Check
    {
      name: "intake_size_top_casing",
      message: addStackPrefixToMessage(
        `Clearance Error: Intake too large for top casing`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        valuesExist([
          wellProperties.value?.r_top_casing?.drift_inches,
          wellProperties.value?.top_casing_length_feet
        ]) && calcs.orderedIntakes.value.length > 0,
      isInvalid: calcs.orderedIntakes.value.some(xref => {
        return (
          wellProperties.value?.r_top_casing?.drift_inches -
            calcs.intakeCalculations.individual.value[xref.id]
              .maxCalculatedOuterDiameterIn <
          MIN_CLEARANCE
        );
      }),
      affectedXrefIds: calcs.orderedIntakes.value
        .filter(xref => {
          return (
            wellProperties.value?.r_top_casing?.drift_inches -
              calcs.intakeCalculations.individual.value[xref.id]
                .maxCalculatedOuterDiameterIn <
            MIN_CLEARANCE
          );
        })
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
    },
    {
      name: "intake_size_bypass_tubing_top_casing",
      message: addStackPrefixToMessage(
        `Clearance Error: Intake too large with bypass tubing for top casing`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        valuesExist([
          wellProperties.value?.r_top_casing?.drift_inches,
          wellProperties.value?.top_casing_length_feet
        ]) &&
        calcs.orderedIntakes.value.length > 0 &&
        !!equipment.value?.use_uphole_y_tool &&
        calcs.orderedYToolBypassTubings.value.length > 0,
      isInvalid: calcs.orderedIntakes.value.some(xref => {
        return (
          wellProperties.value?.r_top_casing?.drift_inches -
            (calcs.intakeCalculations.individual.value[xref.id].xrefIntake
              .r_intake?.outer_diameter_inches +
              calcs.yToolBypassTubingMaxODIn.value) <
          MIN_CLEARANCE
        );
      }),
      affectedXrefIds: calcs.orderedIntakes.value
        .filter(xref => {
          return (
            wellProperties.value?.r_top_casing?.drift_inches -
              (calcs.intakeCalculations.individual.value[xref.id].xrefIntake
                .r_intake?.outer_diameter_inches +
                calcs.yToolBypassTubingMaxODIn.value) <
            MIN_CLEARANCE
          );
        })
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
    },
    {
      name: "intake_size_bottom_casing",
      message: addStackPrefixToMessage(
        `Clearance Error: Intake too large for bottom casing`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        valuesExist([
          wellProperties.value?.r_bottom_casing?.drift_inches,
          wellProperties.value?.bottom_casing_length_feet
        ]) &&
        calcs.equipmentLengths.bottomOfIntakesDepthFt.value >
          wellProperties.value?.top_casing_length_feet &&
        calcs.orderedIntakes.value.length > 0,
      isInvalid: calcs.orderedIntakes.value.some(xref => {
        return (
          wellProperties.value?.r_bottom_casing?.drift_inches -
            calcs.intakeCalculations.individual.value[xref.id]
              .maxCalculatedOuterDiameterIn <
          MIN_CLEARANCE
        );
      }),
      affectedXrefIds: calcs.orderedIntakes.value
        .filter(xref => {
          return (
            wellProperties.value?.r_bottom_casing?.drift_inches -
              calcs.intakeCalculations.individual.value[xref.id]
                .maxCalculatedOuterDiameterIn <
            MIN_CLEARANCE
          );
        })
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
    },
    {
      name: "intake_size_bypass_tubing_bottom_casing",
      message: addStackPrefixToMessage(
        `Clearance Error: Intake too large with bypass tubing for bottom casing`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        valuesExist([
          wellProperties.value?.r_bottom_casing?.drift_inches,
          wellProperties.value?.bottom_casing_length_feet
        ]) &&
        calcs.equipmentLengths.bottomOfIntakesDepthFt.value >
          wellProperties.value?.top_casing_length_feet &&
        calcs.orderedIntakes.value.length > 0 &&
        !!equipment.value?.use_uphole_y_tool &&
        calcs.orderedYToolBypassTubings.value.length > 0,
      isInvalid: calcs.orderedIntakes.value.some(xref => {
        return (
          wellProperties.value?.r_bottom_casing?.drift_inches -
            (calcs.intakeCalculations.individual.value[xref.id].xrefIntake
              .r_intake?.outer_diameter_inches +
              calcs.yToolBypassTubingMaxODIn.value) <
          MIN_CLEARANCE
        );
      }),
      affectedXrefIds: calcs.orderedIntakes.value
        .filter(xref => {
          return (
            wellProperties.value?.r_bottom_casing?.drift_inches -
              (calcs.intakeCalculations.individual.value[xref.id].xrefIntake
                .r_intake?.outer_diameter_inches +
                calcs.yToolBypassTubingMaxODIn.value) <
            MIN_CLEARANCE
          );
        })
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
    },
    // Shaft Limit check
    {
      name: "intake_shaft_limit_hs",
      message: addStackPrefixToMessage(
        `HP greater than Intake Shaft Limit (HS)`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: calcs.isScenarioEspShore.value,
      isInvalid: calcs.orderedIntakes.value.some(
        xref =>
          calcs.intakeCalculations.individual.value[xref.id]
            .shaftPowerLimitHighStrengthHP <
          calcs.motorCalculations.totalRequiredBrakeHP.value
      ),
      affectedXrefIds: calcs.orderedIntakes.value
        .filter(
          xref =>
            calcs.intakeCalculations.individual.value[xref.id]
              .shaftPowerLimitHighStrengthHP <
            calcs.motorCalculations.totalRequiredBrakeHP.value
        )
        .map(xref => xref.id),
      disablesRules: ["intake_shaft_limit_std"],
      rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
    },
    {
      name: "intake_shaft_limit_std",
      message: addStackPrefixToMessage(
        `HP greater than Intake Shaft Limit (STD), high-strength intake shaft required`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        valuesExist([calcs.motorCalculations.totalRequiredBrakeHP.value]) &&
        calcs.isScenarioEspShore.value,
      isInvalid: calcs.orderedIntakes.value.some(
        xref =>
          !xref.use_high_strength_shaft &&
          calcs.intakeCalculations.individual.value[xref.id]
            .shaftPowerLimitStandardHP <
            calcs.motorCalculations.totalRequiredBrakeHP.value
      ),
      affectedXrefIds: calcs.orderedIntakes.value
        .filter(
          xref =>
            !xref.use_high_strength_shaft &&
            calcs.intakeCalculations.individual.value[xref.id]
              .shaftPowerLimitStandardHP <
              calcs.motorCalculations.totalRequiredBrakeHP.value
        )
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
    },
    {
      name: "intake_use_natural_gas_separation",
      message: addStackPrefixToMessage(
        "It's recommended to use Calculated Natural Separation",
        stack,
        equipment
      ),
      severity: Severity.warn,
      isActive: calcs.orderedIntakes.value.length > 0,
      isInvalid:
        !calcs.intakeCalculations.aggregate.intakesContainNonBoltOn.value &&
        parameters.value?.natural_gas_separation_override_type ===
          Override_Options_Enum.Disable &&
        !parameters.value.intake_packer_installed,
      rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
    }
  ];
};
